import React, { useEffect, useState } from "react";
import API from "./Context/api";
import Spinner from "./Helpers/Spinner";
import { useAuth } from "./Context/AuthContext";

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuth();
  const userid = user?.userId ?? "";

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await API.get(`tasks/assigned/${userid}`); // Adjust endpoint as needed
        setTasks(res.data);
        setLoading(false);
        console.log(res.data);
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
        setError("Failed to load tasks");
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const getTaskAgeInDays = (dateAssigned) => {
    if (!dateAssigned) return "N/A";
    const assignedDate = new Date(dateAssigned);
    const today = new Date();
    const diffTime = today - assignedDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filtered tasks based on title
  const filteredTasks = tasks.filter((task) =>
    task.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openDeleteModal = (taskId) => {
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setTaskToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    setIsDeleting(true);
    try {
      await API.delete(`/tasks/${taskToDelete}`);
      setTasks((prev) => prev.filter((task) => task.taskId !== taskToDelete));
      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete task:", error);
      alert("Failed to delete task. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveEditedTask = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        Title: editFormData.title,
        TaskID: taskToEdit.taskId,
        AssignedTo: editFormData.assignedTo,
        AssignedToName: editFormData.assignedToName,
        Description: editFormData.description,
        Status: editFormData.status,
        CreatedBy: editFormData.CreatedBy,
        DateAssigned: editFormData.dateAssigned,
      };
      await API.put(`/tasks/${taskToEdit.taskId}`, payload);

      setTasks((prev) =>
        prev.map((t) =>
          t.taskId === taskToEdit.taskId ? { ...t, ...editFormData } : t
        )
      );

      setTaskToEdit(null);
    } catch (err) {
      console.error("Failed to update task:", err);
      alert("Failed to update task.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 text-lg animate-pulse">Loading...</div>
      </div>
    );
  }

  // Render error
  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="h-full flex flex-col">
      {!loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500 text-lg animate-pulse">
            No tasks assigned to you.
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <h3 className="text-xl font-semibold mb-2 text-gray-900">Tasks</h3>

          {/* Search and Add User */}
          <div className="py-2 mb-4 flex items-center justify-between gap-4 flex-wrap">
            <input
              type="text"
              placeholder="Search by Title..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="p-2 border rounded w-full md:w-64 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {/* Table View (Desktop) */}
            <div className="hidden sm:block bg-white rounded-lg shadow-sm">
              <table className="w-full text-left table-fixed">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Date Assigned
                    </th>
                    <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      Aging (days)
                    </th>
                    {/* <th className="w-[12%] px-4 py-3 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {filteredTasks.map((task, index) => (
                    <tr
                      key={task.taskId}
                      className={`text-sm ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {task.title}
                      </td>
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {task.status}
                      </td>
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {task.assignedToName}
                      </td>
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {task.description}
                      </td>
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {new Date(task.dateAssigned).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-700 break-words">
                        {getTaskAgeInDays(task.dateAssigned)}
                      </td>
                      {/* <td className="px-4 py-3">
                    <button
                      onClick={() => openDeleteModal(task.taskId)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition duration-200"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => {
                        setTaskToEdit(task);
                        setEditFormData({
                          title: task.title,
                          status: task.status,
                          assignedTo: task.assignedTo,
                          assignedToName: task.assignedToName,
                          description: task.description,
                          dateAssigned: task.dateAssigned?.split("T")[0], // format as yyyy-mm-dd
                        });
                      }}
                      className="bg-indigo-500 text-white px-3 py-1 rounded hover:bg-indigo-600"
                    >
                      Edit
                    </button>
                  </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card View (Mobile) */}
            <div className="md:hidden space-y-4">
              {filteredTasks.map((task) => (
                <div
                  key={task.taskId}
                  className="bg-white p-4 shadow rounded-lg"
                >
                  <h3 className="text-lg font-bold">{task.title}</h3>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Status:</strong> {task.status}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Assigned To:</strong> {task.assignedToName}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Date Assigned:</strong>{" "}
                    {new Date(task.dateAssigned).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Aging (days):{getTaskAgeInDays(task.dateAssigned)}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    {task.description}
                  </p>
                  {/* <button
                onClick={() => openDeleteModal(task.taskId)}
                className="mt-2 bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition duration-200"
              >
                Delete
              </button>

              <button
                onClick={() => {
                  setTaskToEdit(task);
                  setEditFormData({
                    title: task.title,
                    status: task.status,
                    assignedTo: task.assignedTo,
                    assignedToName: task.assignedToName,
                    description: task.description,
                    dateAssigned: task.dateAssigned?.split("T")[0], // format as yyyy-mm-dd
                  });
                }}
                className="bg-indigo-500 text-white px-3 py-1 rounded hover:bg-indigo-600"
              >
                Edit
              </button> */}
                </div>
              ))}
            </div>
          </div>

          {taskToEdit && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md space-y-4">
                <h2 className="text-lg font-semibold">Edit Task</h2>

                <input
                  name="title"
                  value={editFormData.title}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, title: e.target.value })
                  }
                  placeholder="Title"
                  className="w-full border p-2 rounded"
                />

                <input
                  name="status"
                  value={editFormData.status}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, status: e.target.value })
                  }
                  placeholder="Status"
                  className="w-full border p-2 rounded"
                />

                <input
                  name="assignedToName"
                  value={editFormData.assignedToName}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      assignedToName: e.target.value,
                    })
                  }
                  placeholder="Assigned To Name"
                  className="w-full border p-2 rounded"
                />

                <input
                  name="description"
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Description"
                  className="w-full border p-2 rounded"
                />

                <input
                  type="date"
                  name="dateAssigned"
                  value={editFormData.dateAssigned}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      dateAssigned: e.target.value,
                    })
                  }
                  className="w-full border p-2 rounded"
                />

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setTaskToEdit(null)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditedTask}
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded text-white ${
                      isSubmitting
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isDeleteModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
                <p className="mb-4">
                  Are you sure you want to delete this task?
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleDeleteTask}
                    disabled={isDeleting}
                    className={`${
                      isDeleting ? "bg-gray-300" : "bg-red-500 hover:bg-red-600"
                    } text-white px-4 py-2 rounded`}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                  <button
                    onClick={closeDeleteModal}
                    disabled={isDeleting}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Tasks;
